// src/feature-module/student/GradeConverter.tsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaCalculator } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const iCls = "w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:border-[#4A729A] bg-white";

const FL: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
    {children}
  </div>
);

const UNI_GRADES = [
  { label: "A+ – Outstanding (4.00)", value: "4.00", pct: "80 – 100 %" },
  { label: "A – Excellent (3.75)",    value: "3.75", pct: "75 – 79 %" },
  { label: "A- – Very Good (3.50)",   value: "3.50", pct: "70 – 74 %" },
  { label: "B+ – Good (3.25)",        value: "3.25", pct: "65 – 69 %" },
  { label: "B – Above Average (3.00)",value: "3.00", pct: "60 – 64 %" },
  { label: "B- – Average (2.75)",     value: "2.75", pct: "55 – 59 %" },
  { label: "C+ – Below Average (2.50)",value:"2.50", pct: "50 – 54 %" },
  { label: "C – Pass (2.25)",         value: "2.25", pct: "45 – 49 %" },
  { label: "D – Marginal Pass (2.00)",value: "2.00", pct: "40 – 44 %" },
];

const SSC_GRADES = [
  { label: "A+ (5.00)", value: "5.00", pct: "80 – 100 %" },
  { label: "A (4.00)",  value: "4.00", pct: "70 – 79 %" },
  { label: "A- (3.50)", value: "3.50", pct: "60 – 69 %" },
  { label: "B (3.00)",  value: "3.00", pct: "50 – 59 %" },
  { label: "C (2.00)",  value: "2.00", pct: "40 – 49 %" },
  { label: "D (1.00)",  value: "1.00", pct: "33 – 39 %" },
];

const GradeConverter: React.FC = () => {
  const [calc, setCalc] = useState({ max: "", min: "", yours: "", preset: "" as "" | "uni" | "ssc" });

  const letterGrades = calc.preset === "uni" ? UNI_GRADES : calc.preset === "ssc" ? SSC_GRADES : [];
  const max = parseFloat(calc.max);
  const min = parseFloat(calc.min);
  const yours = parseFloat(calc.yours);
  const allFilled = calc.max && calc.min && calc.yours;
  let result: number | null = null;
  if (allFilled && !isNaN(max) && !isNaN(min) && !isNaN(yours) && max !== min) {
    result = 1 + 3 * (max - yours) / (max - min);
  }
  const gradeLabel = result === null ? null
    : result <= 1.5 ? "Sehr gut" : result <= 2.5 ? "Gut"
    : result <= 3.5 ? "Befriedigend" : result <= 4.0 ? "Ausreichend" : "Nicht bestanden";
  const gradeColor = result === null ? "" : result <= 1.5 ? "#16a34a" : result <= 2.5 ? "#2563eb" : result <= 3.5 ? "#ca8a04" : result <= 4.0 ? "#ea580c" : "#dc2626";
  const gradeBg = result === null ? "" : result <= 1.5 ? "#f0fdf4" : result <= 2.5 ? "#eff6ff" : result <= 3.5 ? "#fefce8" : result <= 4.0 ? "#fff7ed" : "#fef2f2";

  const refTable = calc.preset === "uni" ? UNI_GRADES : calc.preset === "ssc" ? SSC_GRADES : null;

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaCalculator />}
        title="Notenumrechnung"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "CV Builder", to: "/student/cv-builder" },
          { label: "Notenumrechnung" },
        ]}
      />

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-6">

        {/* Intro */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h2 className="text-base font-bold text-gray-800 mb-2">Bayerische Formel</h2>
          <p className="text-sm text-gray-600 leading-relaxed mb-3">
            Rechnen Sie Ihre bangladeschische Note ins deutsche Notensystem um. Im deutschen System ist <strong>1,0</strong> die beste Note und <strong>4,0</strong> die unterste Bestehensnote.
          </p>
          <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm font-mono text-gray-700 border border-gray-200">
            Deutsche Note = 1 + 3 × (Maximalnote − Ihre Note) ÷ (Maximalnote − Unterste Bestehensnote)
          </div>
          <p className="text-xs text-gray-400 mt-2 italic">Das Ergebnis ist unverbindlich und dient nur einer ersten Orientierung.</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
          <h3 className="font-bold text-gray-700">Rechner</h3>

          {/* Preset */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-2">1. Notensystem wählen</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setCalc({ max: "4.00", min: "2.00", yours: "", preset: "uni" })}
                className={`text-left text-sm px-4 py-3 rounded-xl border-2 font-semibold transition-all ${calc.preset === "uni" ? "border-[#4A729A] bg-[#4A729A] text-white" : "border-gray-200 text-gray-700 hover:border-[#4A729A]"}`}
              >
                Universität / Diplom
                <div className={`text-xs font-normal mt-0.5 ${calc.preset === "uni" ? "text-white/70" : "text-gray-400"}`}>CGPA max. 4,0 · Bestehen ab 2,0</div>
              </button>
              <button
                onClick={() => setCalc({ max: "5.00", min: "1.00", yours: "", preset: "ssc" })}
                className={`text-left text-sm px-4 py-3 rounded-xl border-2 font-semibold transition-all ${calc.preset === "ssc" ? "border-[#4A729A] bg-[#4A729A] text-white" : "border-gray-200 text-gray-700 hover:border-[#4A729A]"}`}
              >
                SSC / HSC (Schule)
                <div className={`text-xs font-normal mt-0.5 ${calc.preset === "ssc" ? "text-white/70" : "text-gray-400"}`}>GPA max. 5,0 · Bestehen ab 1,0</div>
              </button>
            </div>
          </div>

          {/* Letter grade dropdown */}
          {letterGrades.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-2">2. Notenbezeichnung wählen</div>
              <select
                value={calc.yours}
                onChange={e => setCalc(g => ({ ...g, yours: e.target.value }))}
                className={iCls}
              >
                <option value="">– Ihre Note auswählen –</option>
                {letterGrades.map(g => (
                  <option key={g.value} value={g.value}>{g.label} &nbsp;({g.pct})</option>
                ))}
              </select>
            </div>
          )}

          {/* Manual inputs */}
          <div>
            <div className="text-xs font-semibold text-gray-500 mb-2">{letterGrades.length > 0 ? "Oder manuell eingeben" : "Werte eingeben"}</div>
            <div className="grid grid-cols-3 gap-3">
              <FL label="Maximalnote">
                <input type="number" step="any" min="0" value={calc.max} onChange={e => setCalc(g => ({ ...g, max: e.target.value, preset: "" }))} placeholder="z.B. 4.0" className={iCls} />
              </FL>
              <FL label="Unterste Bestehensnote">
                <input type="number" step="any" min="0" value={calc.min} onChange={e => setCalc(g => ({ ...g, min: e.target.value, preset: "" }))} placeholder="z.B. 2.0" className={iCls} />
              </FL>
              <FL label="Ihre Note (GPA)">
                <input type="number" step="any" min="0" value={calc.yours} onChange={e => setCalc(g => ({ ...g, yours: e.target.value }))} placeholder="z.B. 3.5" className={iCls} />
              </FL>
            </div>
          </div>

          {/* Result */}
          {result !== null && (
            <div className="rounded-xl p-5 flex items-center gap-5 border" style={{ background: gradeBg, borderColor: gradeColor + "44" }}>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: gradeColor }}>Deutsche Note</div>
                <div className="text-5xl font-bold" style={{ color: gradeColor }}>{result.toFixed(2).replace(".", ",")}</div>
              </div>
              <div className="text-xl font-semibold" style={{ color: gradeColor }}>{gradeLabel}</div>
            </div>
          )}
          {allFilled && result === null && (
            <p className="text-xs text-red-500">Ungültige Eingabe – Maximalnote und Bestehensnote dürfen nicht gleich sein.</p>
          )}
        </div>

        {/* Reference table */}
        {refTable && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="font-bold text-gray-700 mb-3">
              Notentabelle – {calc.preset === "uni" ? "Universität / Diplom (CGPA 4.0)" : "SSC / HSC (GPA 5.0)"}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Note</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">GPA</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Prozent</th>
                    <th className="text-left px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">Deutsche Note</th>
                  </tr>
                </thead>
                <tbody>
                  {refTable.map(g => {
                    const maxV = parseFloat(calc.max);
                    const minV = parseFloat(calc.min);
                    const gV = parseFloat(g.value);
                    const german = (!isNaN(maxV) && !isNaN(minV) && maxV !== minV)
                      ? (1 + 3 * (maxV - gV) / (maxV - minV)).toFixed(2).replace(".", ",")
                      : "–";
                    const germanNum = parseFloat(german.replace(",", "."));
                    const col = isNaN(germanNum) ? "#888" : germanNum <= 1.5 ? "#16a34a" : germanNum <= 2.5 ? "#2563eb" : germanNum <= 3.5 ? "#ca8a04" : "#ea580c";
                    return (
                      <tr key={g.value} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-semibold text-gray-800">{g.label.split(" – ")[0].split(" (")[0]}</td>
                        <td className="px-3 py-2.5 text-gray-600">{g.value}</td>
                        <td className="px-3 py-2.5 text-gray-500 text-xs">{g.pct}</td>
                        <td className="px-3 py-2.5 font-bold" style={{ color: col }}>{german}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="text-center">
          <Link to="/student/cv-builder" className="inline-flex items-center gap-2 text-sm text-[#4A729A] hover:underline">
            Zum CV Builder
          </Link>
        </div>
      </main>
    </div>
  );
};

export default GradeConverter;
